import React, { useState } from "react";
import { MailOutlined } from "@ant-design/icons";
import { CiCloudOff } from "react-icons/ci";
import { AiFillEdit } from "react-icons/ai";
import { PiMonitorFill, PiNutFill, PiScanDuotone } from "react-icons/pi";
import { Tooltip } from "antd";
import { FaMedkit, FaUser, FaHandshake } from "react-icons/fa";
import { MdArchive, MdEditDocument, MdUnarchive } from "react-icons/md";
import DynamicFormModal from "../../dynamic-modal/DynamicFormModal";
import { LuMonitorOff, LuPhone } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import {
  AppDispatch,
  getOrganizationAdminRequest,
  Organization,
} from "@src/redux";
import { useDispatch } from "react-redux";
import { useCheckPageComponentAccess } from "@src/hooks/index";

interface ClientDetailsCardProps {
  client: Organization;
}

const ClientDetailsCard: React.FC<ClientDetailsCardProps> = ({ client }) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [formType, setFormType] = useState<string>("");
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const showModal = (type: string) => {
    if (type === "edit") {
      navigate(`/settings/client/${client.id}`);
    }
    if (type === "admin") {
      dispatch(getOrganizationAdminRequest({ id: client?.id || "" }));
    }
    setFormType(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const getModalTitle = (type: string): string => {
    switch (type) {
      case "offline_readings":
        return "Upload Offline Readings";
      case "archive":
        return "Archive";
      case "un_archive":
        return "Un Archive";
      case "sla":
        return "SLA Agreement";
      case "attributes":
        return `${client.name} Levels`;
      case "kits":
        return "Kits";
      case "admin":
        return `${client.name} - Edit Admin`;
      case "sla_agreement":
        return "SLA Agreement";
      case "sla_agreement_disable":
        return "SLA Agreement";
      case "edit":
        return "Edit Client";
      default:
        return "";
    }
  };

  const phoneDisplay = `${
    client?.mobile?.country_short_code
      ? "+" + client?.mobile?.country_short_code
      : ""
  } ${client?.mobile?.number || ""}`.trim();

  return (
    <div className={`client-card ${client?.isArchived ? "client-card-archived" : ""}`}>
      {/* Gradient accent bar with shimmer */}
      <div className="client-card-gradient-top" />

      {/* Header Section */}
      <div className="client-card-top">
        <div className="client-card-top-left">
          <div className="client-avatar">
            <span className="client-avatar-letter">
              {client?.name?.charAt(0)?.toUpperCase() || "C"}
            </span>
            <span className="client-avatar-ring" aria-hidden="true" />
          </div>
          <div className="client-card-title-wrap">
            <h3 className="client-card-title">{client?.name}</h3>
            <span className={`client-status-dot ${client?.isActivated ? "dot-active" : "dot-inactive"}`}>
              {client?.isActivated ? (
                <><PiMonitorFill /> Active</>
              ) : (
                <><LuMonitorOff /> Inactive</>
              )}
            </span>
          </div>
        </div>
        <div className="client-card-actions">
          {useCheckPageComponentAccess("upload_offline_reading_action") && (
            <Tooltip title="Upload Offline Readings" placement="bottom">
              <button className="client-action-btn action-red" onClick={() => showModal("offline_readings")}>
                <CiCloudOff />
              </button>
            </Tooltip>
          )}
          {client?.isArchived && useCheckPageComponentAccess("archive_client_action") && (
            <Tooltip title="Un Archive" placement="bottom">
              <button className="client-action-btn action-green" onClick={() => showModal("un_archive")}>
                <MdUnarchive />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("archive_client_action") && !client?.isArchived && (
            <Tooltip title="Archive" placement="bottom">
              <button className="client-action-btn action-pink" onClick={() => showModal("archive")}>
                <MdArchive />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("sla_agreement_button") && (
            <Tooltip title={client.requireSlaAcceptance ? "Disable Need SLA" : "Enable Need SLA"} placement="bottom">
              <button className="client-action-btn action-teal" onClick={() => showModal("sla")}>
                <MdEditDocument />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("attributes_action") && (
            <Tooltip title="Levels" placement="bottom">
              <button className="client-action-btn action-purple" onClick={() => showModal("attributes")}>
                <PiNutFill />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("add_client_kit_action") && (
            <Tooltip title="Kits" placement="bottom">
              <button className="client-action-btn action-green" onClick={() => showModal("kits")}>
                <FaMedkit />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("edit_admin_action") && (
            <Tooltip title="Admin" placement="bottom">
              <button className="client-action-btn action-orange" onClick={() => showModal("admin")}>
                <FaUser />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("sla_agreement_button") && (
            <Tooltip title="SLA Agreement" placement="bottom">
              <button className="client-action-btn action-brown" onClick={() => showModal("sla_agreement")}>
                <FaHandshake />
              </button>
            </Tooltip>
          )}
          {useCheckPageComponentAccess("edit_client_kit_action") && (
            <Tooltip title="Edit" placement="bottom">
              <button className="client-action-btn action-blue" onClick={() => showModal("edit")}>
                <AiFillEdit />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Contact Info Footer */}
      <div className="client-card-bottom">
        <div className="client-info-chip">
          <span className="chip-icon chip-icon-scan"><PiScanDuotone /></span>
          <div className="chip-content">
            <span className="chip-label">Client ID</span>
            <span className="chip-text">{client?.usernamePrefix || "-"}</span>
          </div>
        </div>
        <div className="client-info-chip">
          <span className="chip-icon chip-icon-phone"><LuPhone /></span>
          <div className="chip-content">
            <span className="chip-label">Phone</span>
            <span className="chip-text">{phoneDisplay || "-"}</span>
          </div>
        </div>
        <div className="client-info-chip">
          <span className="chip-icon chip-icon-mail"><MailOutlined /></span>
          <div className="chip-content">
            <span className="chip-label">Email</span>
            <span className="chip-text">{client?.email || "-"}</span>
          </div>
        </div>
      </div>

      <DynamicFormModal
        visible={isModalVisible}
        onCancel={handleCancel}
        formType={formType}
        title={getModalTitle(formType)}
        item={client}
      />
    </div>
  );
};

export default ClientDetailsCard;
